import _ from 'lodash';
import moment from 'moment';
import {Injectable} from '@angular/core';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {Store} from '@ngrx/store';
import {map, tap} from 'rxjs/operators';

import {BrowserCacheService} from "@ispm/core/services/browser-cache.service";
import {ConfigurationStoreHandler} from '../../../configuration/services/configuration-store-handler.service';
import {DiagnosticOrderActionsService} from './diagnostic-order-actions.service';
import {EnumService} from '@ispm/user/services/enum.service';
import {HttpInterceptorService} from '@ispm/core/services/http-interceptor.service';
import {IDefinition} from '../../../configuration/interfaces/definition.interface';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {InitTestState} from '../store/test.actions';
import {NotificationMessageService} from '@ispm/core/components/customise-notification/notifications-message.service';
import {TestRunningMode} from '../interfaces/test-running-mode.enum';
import {TranslateService} from '@ngx-translate/core';
import {WorkflowConfigService} from '@ispm/workflow/services/workflow-config.service';
import {activeTabObject} from "@ispm/test/constants/Tabs.constants";

@Injectable()
export class TestService {
  static testBase = '/test';
  static workflowBase = '/workflow';

  public changeEmitted$: Observable<any>;
  public isFilterPanelClose = true;
  public modalitiesData = {filterData: {}};
  public patientTests = null;
  public prefetchInProgress = false;
  public testConfiguration: any;
  public testInfoCollections: any;

  private _activeTab = activeTabObject.TEST_INFO;
  private actionButtonsToggle: ReplaySubject<any> = new ReplaySubject<any>();
  private cached: Map<string, string> = new Map();
  private emitChangeSource = new Subject<any>();
  private testGenericUrl: string = '/api/v2/test';

  constructor(
    private _configurationStoreHandler: ConfigurationStoreHandler,
    private _http: HttpInterceptorService,
    private browserCacheService: BrowserCacheService,
    private diagnosticOrderActionsService: DiagnosticOrderActionsService,
    private enumService: EnumService,
    private notificationMessageService: NotificationMessageService,
    private store$: Store<IgenomicsState>,
    private translateService: TranslateService,
    private workflowConfigService: WorkflowConfigService,
  ) {
    this.workflowConfigService.addCacheHandler(this.clearWorkflowData.bind(this));
    this.changeEmitted$ = this.emitChangeSource.asObservable();
  }

  protected clearWorkflowData() {
    this.clearTests();
    this.store$.dispatch(new InitTestState());
    this.diagnosticOrderActionsService.initDiagnosticOrderState();
  }

  public checkDecision() {
    this.emitChangeSource.next("");
  }

  public archiveTest(workflowId) {
    return this._http.post(`${TestService.workflowBase}/archiveAnalysis`, {workflowId}, true)
      .map(success => {
        setTimeout(() => {
          this.notificationMessageService.success('Test removed from your worklist successfully!');
        }, 0);
        return success.json();
      })
      .catch(err => {
        setTimeout(() => {
          this.notificationMessageService.error('error while archiving test');
        }, 0);
        return err;
      });
  }

  public getDiagnosticSummaryByRevision(revisionId: string): Observable<any> {
    return this._http.get(TestService.testBase + '/diagnosticSummary', {revisionId: revisionId}, true)
      .pipe(
        map(res => res.json())
      );
  }

  public updateDiagnosticSummary(diagnosticSummary: any): Observable<any> {
    return this._http.put(TestService.testBase + '/diagnosticSummary/' + diagnosticSummary.id, diagnosticSummary, true)
      .pipe(
        map(res => res.json())
      );
  }

  public createDiagnosticSummary(diagnosticSummary: any, revisionId: string): Observable<any> {
    return this._http.post(TestService.testBase + '/diagnosticSummary', diagnosticSummary, true, null, {revisionId: revisionId})
      .pipe(
        map(res => res.json())
      );
  }

  public upsertDiagnosticSummary(diagnosticSummary: any, revisionId?: string): Observable<any> {
    return diagnosticSummary.id
      ? this.updateDiagnosticSummary(diagnosticSummary)
      : this.createDiagnosticSummary(diagnosticSummary, revisionId);
  }

  public findTestConfiguration(testId: string): Observable<any> {
    const request = this._http.get(this.testGenericUrl + '/' + testId)
      .map(data => data.json())
      .map(item => {
        if (!item) {
          this.notificationMessageService.error('Test configuration not found');
        }

        return item;
      })
      .publish();
    request.connect();

    return request;
  }

  public createTest(diagnosticOrder: any, additionalData: any) {
    const url = diagnosticOrder.runningMode && diagnosticOrder.runningMode === TestRunningMode.DRAFT_MODE
      ? this.testGenericUrl + '/create' + diagnosticOrder.runningMode.toLowerCase()
      : this.testGenericUrl;

    return this._http.post(url, {resource: diagnosticOrder, additionalData: additionalData})
      .map(res => {
        return res.json();
      })
      .catch(() => {
        this.notificationMessageService.error('[testService.createTest] Unable to create new test');

        return null;
      }).toPromise();
  }

  public getCachedTest(name: string): Promise<any> {
    const configId = this.cached.get(name);

    if (configId) {
      return this._configurationStoreHandler.getConfigById('Test', configId).take(1).toPromise();
    }
  }

  public putConfig(name, config) {
    this._configurationStoreHandler.setConfiguration(config);
    this.cached.set(name, config.id);
  }

  public isItCached(name: string): any {
    return this.cached.get(name);
  }

  public clearCache() {
    this.cached.clear();
  }

  public selectTestConfiguration(diagnosticOrder, organizationId) {
    return diagnosticOrder.testConfigurations
      ? diagnosticOrder.testConfigurations.find((config) => config.organization == organizationId).testConfiguration
      : diagnosticOrder.workflowRequestTemplateId;
  }

  public clearTests() {
    this.patientTests = null;
    this.testConfiguration = null;
    this.updateConfigurationStore(); // update ConfigurationStoreHandler when testConfiguration is cleared
  }

  get activeTab(): { name: string; index: number; alias: string } {
    return this._activeTab;
  }

  set activeTab(value: { name: string; index: number; alias: string }) {
    this._activeTab = value?? activeTabObject.TEST_INFO;
  }

  public getTestConfiguration(testTypeId: string) {
    return this._http.get(TestService.testBase + '/getTestConfiguration', {
      testTypeId: testTypeId,
      cacheId: this.browserCacheService.getCacheId()
    })
      .pipe(
        tap(testRes => {
          this.setTestConfiguration(testRes.json());
        })
      ).toPromise();
  }

  public setTestConfiguration(testConfig) {
    this.testConfiguration = testConfig;

    for (let modalityKey in this.testConfiguration.displayColumns) {
      this.testConfiguration.displayColumns[modalityKey].forEach((col) => {
        //"|| col.columnName;" was removed because, here, if translation is empty, no text should be displayed (and not the column name)
        col.translateColumnName = this.translateService.instant('aberration.' + col.columnName);// || col.columnName;
      })
    }

    this.updateConfigurationStore();
  }

  public updateConfigurationStore() {
    this._configurationStoreHandler.setConfiguration(this.testConfiguration);
  }

  public isXhtmlTemplateEnabled(xhtmlTemplateType, defIfNone) {
    return this.isTemplateEnabled(xhtmlTemplateType, 'testConfiguration.xHtmlReportTemplates', defIfNone);
  };

  public isTaskDispatchTemplateEnabled (taskDispatchTemplateType, defIfNone) {
    return this.isTemplateEnabled(taskDispatchTemplateType, 'testConfiguration.taskDispatchTemplates', defIfNone);
  };

  public isTemplateEnabled(templateType, templatesConfigName, defIfNone = false) {

    // search in the test's configuration a a template that matches the given templateType and which is marked as enabled
    if (_.has(this, templatesConfigName)) {
      const templatesConfig = _.get(this, templatesConfigName);

      for (let key in templatesConfig) {
        const template = templatesConfig[key];

        // if template type matches - return false if <reference> is not given OR that template's' settings is specifically disabled
        if (template.type && template.type == templateType) {
          return !(!template.reference || (template.settings && !template.settings.enabled));
        }
      }
    }

    // return the default to use in case no matching template is found
    return defIfNone;
  }

   /**
   * return an Observable that holds the updated <service.testConfiguration> value.
   *
   * Note: <service.testConfiguration> is set in 2 places:
   *          (1) clearing its value at clearTests() method
   *          (2) reading its updated value from the server at getTestConfiguration() method. The updated value is than
   *              being stored to the <ConfigurationStoreHandler> storage.
   *
   *        It is possible that clients may try reading the given <service.testConfiguration> value right after it was
   *          cleared BUT before its updated value was read from the server. In this case, if not handled, a Null exception
   *          is expected. This method ensures that clients will get the appropriate value when it is set
   */

  public readTestConfiguration(): Observable<IDefinition> {
    return this._configurationStoreHandler.getCurrentConfig()
      .first((currentConfig) => currentConfig && currentConfig !== null);
  }

  public getCurrentDynamicSearchableLinksConfig() {
    if (this.patientTests && this.patientTests.current) {
      if ("searchableFieldsConfig" in this.patientTests.current) {
        return this.patientTests.current.searchableFieldsConfig;
      }
    }
    return null;
  }

  public loadTestInfoCollections() {
    if (this.testInfoCollections)
      return new Promise((resolve) => {
        resolve(this.testInfoCollections);
      });
    else {
      let testInfoCollections = {};
      let promises = [];
      promises.push(this.createPromise('getSpecimenTypes', 'specimenTypes', testInfoCollections));
      promises.push(this.createPromise('getTumorGrades', 'tumorGrades', testInfoCollections));
      promises.push(this.createPromise('getTumorStages', 'tumorStages', testInfoCollections));
      promises.push(this.createPromise('getSpecimenCollections', 'specimenCollections', testInfoCollections));
      promises.push(this.createPromise('getTreatementStatuses', 'treatmentStatuses', testInfoCollections));
      promises.push(this.createPromise('getPrinters', 'printers', testInfoCollections));

      return Promise.all(promises).then(() => {
        this.testInfoCollections = testInfoCollections;
      });

    }
  }

  public getLocalDateTime() {
    return moment(moment.now()).format('DD MMM YYYY');
  }

  public getLocalTime() {
    return moment(moment.now()).format('hh:mm A');
  }

  public testConfigUpdate(testId, config) {
    return this._http.put(TestService.testBase + '/configuration/' + testId, config)
      .map(res => {
        return res;
      }).toPromise();
  }

  public updateButtonState(buttonEnum, buttonType = 'case') {
    return this.actionButtonsToggle.next({
      buttonEnum,
      buttonType
    });
  }

  public createPromise(functionName, propertyName, dataObject) {
    return new Promise((resolve, reject) => {
      this.enumService[functionName]()
        .subscribe(
          (res: any) => {
            dataObject[propertyName] = res.body;
            resolve(true);
          },
          (error: any) => {
            console.log("error in load test info collection for: " + propertyName + ". Error: " + error.message);
            reject(error);
          }
        );
    });
  }

  public isNavicanControlSample() {
    return this.testConfiguration && this.testConfiguration.isQcTest != undefined && this.testConfiguration.isQcTest == true;
  }

  public getHiddenLevelsOfEvidence() {
    return _.get(this, "testConfiguration.therapyMatch.hiddenLevelOfEvidence", "['Not determined', 'None found']");
  }

  public getDataDescriptorName(mode) {
    return mode.toLowerCase() + 'ModeData';
  }

  public getRunningModeData(mode) {
    return !!mode ? _.get(this, 'testConfiguration.' + this.getDataDescriptorName(mode), {}) : undefined;
  }

  public showTestActions(mode) {
    return !!mode ? _.get(this, 'testConfiguration.' + this.getDataDescriptorName(mode) + '.testBar.showTestActions', true) : true;
  }

  public getDynamicHeaderFields(mode) {
    return !!mode ? _.get(this, 'testConfiguration.' + this.getDataDescriptorName(mode) + '.testBar.dynamicHeaderFields', undefined) :
      _.get(this, 'testConfiguration.dynamicHeaderFields', undefined);
  }

  public hasDynamicHeaderFields(mode) {
    return !!mode ? _.has(this, 'testConfiguration.' + this.getDataDescriptorName(mode) + '.testBar.dynamicHeaderFields') :
      _.has(this, 'testConfiguration.dynamicHeaderFields');
  }

  public getNotifConfigurationByDiagnosticOrder(diagnosticOrder, testConfiguration) {
    if (_.has(diagnosticOrder, 'runningMode') && !!_.get(diagnosticOrder, 'runningMode')) {
      const runningModeData = this.getDataDescriptorName(_.get(diagnosticOrder, 'runningMode'));
      if (_.has(testConfiguration, runningModeData) && _.has(testConfiguration, runningModeData + '.notificationConfig')) {
        let defaultConfig;
        const notifications = testConfiguration[runningModeData]['notificationConfig'];
        let foundByProperty = notifications.find(item => {
            if (_.has(item, 'property') && _.has(item, 'value')) {
              const values = _.get(item, 'value', undefined);
              const property = _.get(item, 'property', undefined);
              if (!!values && Array.isArray(values) && values.length > 0) {
                if (diagnosticOrder && _.has(diagnosticOrder, property)) {
                  const diagOrderPropertyValue = _.get(diagnosticOrder, property, undefined);
                  if (!!diagOrderPropertyValue) {
                    return _.findIndex(values, v => v === diagOrderPropertyValue) >= 0;
                  }
                }
              }
            } else {
              defaultConfig = item;
              return false;
            }
          }
        );
        return !!foundByProperty ? foundByProperty : defaultConfig;
      }
    }
    return undefined;
  }

}
